import { useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  BookImage,
  Plus,
  Trash2,
  Upload,
  Download,
  Cloud,
  CloudUpload,
  FolderDown,
} from 'lucide-react';
import { SiteHeader } from '../components/layout/SiteHeader';
import { Button } from '../components/ui/Button';
import { EmptyState } from '../components/ui/EmptyState';
import { Spinner } from '../components/ui/Spinner';
import { Dialog, DialogContent, DialogFooter } from '../components/ui/Dialog';
import { useProjectsList, useDeleteProject } from '../hooks/useProjectsList';
import { useProjectThumbnailUrl } from '../hooks/useObjectUrl';
import { useAuthStore } from '../state/authStore';
import {
  useCloudProjects,
  useInvalidateCloudProjects,
  type CloudProjectSummary,
} from '../hooks/useCloudProjects';
import { useUiStore } from '../state/uiStore';
import { loadProject } from '../storage/projectRepository';
import { exportProjectBackup, importProjectBackup } from '../storage/backup';
import { migrateProjectToCloud } from '../cloud/migrateToCloud';
import { pullCloudProject } from '../cloud/syncClient';
import { downloadBlob, slugify } from '../lib/download';
import { relativeTimeNl } from '../lib/format';
import { t } from '../i18n';
import type { ProjectMetaRecord } from '../storage/types';
import './projects-page.css';

function ProjectCard({
  meta,
  canSaveToCloud,
  onDeleted,
  onSyncedToCloud,
}: {
  meta: ProjectMetaRecord;
  canSaveToCloud: boolean;
  onDeleted: () => void;
  onSyncedToCloud: () => void;
}) {
  const thumbnailUrl = useProjectThumbnailUrl(meta.id, meta.thumbnailAssetId);
  const deleteProject = useDeleteProject();
  const pushToast = useUiStore((s) => s.pushToast);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [savingToCloud, setSavingToCloud] = useState(false);

  const handleExport = async () => {
    setBusy(true);
    try {
      const result = await loadProject(meta.id);
      if (result.status !== 'ok')
        throw new Error(
          result.status === 'not-found' ? t('errors.projectNotFound') : result.message,
        );
      const blob = await exportProjectBackup(result.project);
      downloadBlob(blob, `${slugify(meta.title)}.fotoboek`);
      pushToast({ kind: 'success', message: `Backup van “${meta.title}” gedownload.` });
    } catch (err) {
      pushToast({
        kind: 'error',
        message: err instanceof Error ? err.message : t('common.unknownError'),
      });
    } finally {
      setBusy(false);
    }
  };

  const handleSaveToCloud = async () => {
    setSavingToCloud(true);
    try {
      const result = await loadProject(meta.id);
      if (result.status !== 'ok')
        throw new Error(
          result.status === 'not-found' ? t('errors.projectNotFound') : result.message,
        );
      const migrated = await migrateProjectToCloud(result.project);
      if (migrated.status === 'ok') {
        pushToast({
          kind: 'success',
          message: t('projects.cloud.saveSuccess', { title: meta.title }),
        });
        onSyncedToCloud();
      } else {
        pushToast({ kind: 'error', message: migrated.message });
      }
    } catch (err) {
      pushToast({
        kind: 'error',
        message: err instanceof Error ? err.message : t('common.unknownError'),
      });
    } finally {
      setSavingToCloud(false);
    }
  };

  const handleDelete = async () => {
    setBusy(true);
    try {
      await deleteProject(meta.id);
      pushToast({ kind: 'info', message: `“${meta.title}” is verwijderd.` });
      onDeleted();
    } finally {
      setBusy(false);
      setConfirmOpen(false);
    }
  };

  return (
    <div className="project-card">
      <Link to={`/editor/${meta.id}`} className="project-card-thumb">
        {thumbnailUrl ? (
          <img src={thumbnailUrl} alt="" />
        ) : (
          <BookImage size={28} strokeWidth={1.5} />
        )}
      </Link>
      <div className="project-card-body">
        <Link to={`/editor/${meta.id}`} className="project-card-title">
          {meta.title}
        </Link>
        <p className="project-card-meta">
          {t('projects.lastEdited')}: {relativeTimeNl(meta.updatedAt)} ·{' '}
          {t('projects.pages', { count: meta.pageCount })}
          {meta.ownerMode === 'cloud' ? (
            <>
              {' · '}
              <Cloud size={11} className="project-card-cloud-icon" />
            </>
          ) : null}
        </p>
      </div>
      <div className="project-card-actions">
        {canSaveToCloud && meta.ownerMode === 'local' ? (
          <Button
            variant="secondary"
            size="sm"
            onClick={handleSaveToCloud}
            disabled={savingToCloud || busy}
            title={t('projects.cloud.saveToCloud')}
          >
            {savingToCloud ? <Spinner size={14} /> : <CloudUpload size={14} />}
          </Button>
        ) : null}
        <Button
          variant="secondary"
          size="sm"
          onClick={handleExport}
          disabled={busy}
          title={t('projects.exportBackup')}
        >
          <Download size={14} />
        </Button>
        <Button
          variant="danger"
          size="sm"
          onClick={() => setConfirmOpen(true)}
          disabled={busy}
          title={t('common.delete')}
        >
          <Trash2 size={14} />
        </Button>
      </div>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent title={t('projects.deleteConfirmTitle')}>
          <p>{t('projects.deleteConfirmBody', { title: meta.title })}</p>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setConfirmOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button variant="danger" onClick={handleDelete} disabled={busy}>
              {busy ? <Spinner size={16} /> : t('common.delete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function CloudProjectCard({
  project,
  onOpened,
}: {
  project: CloudProjectSummary;
  onOpened: () => void;
}) {
  const navigate = useNavigate();
  const pushToast = useUiStore((s) => s.pushToast);
  const [opening, setOpening] = useState(false);

  const handleOpen = async () => {
    setOpening(true);
    try {
      const result = await pullCloudProject(project.id);
      if (result.status === 'ok') {
        onOpened();
        navigate(`/editor/${result.project.id}`);
      } else {
        pushToast({ kind: 'error', message: result.message });
      }
    } finally {
      setOpening(false);
    }
  };

  return (
    <button
      type="button"
      className="project-card project-card-cloud"
      onClick={handleOpen}
      disabled={opening}
    >
      <span className="project-card-thumb">
        {opening ? <Spinner size={24} /> : <BookImage size={28} strokeWidth={1.5} />}
      </span>
      <span className="project-card-body">
        <span className="project-card-title">{project.title}</span>
        <p className="project-card-meta">{relativeTimeNl(project.updatedAt)}</p>
      </span>
      <span className="project-card-actions">
        <FolderDown size={14} />
      </span>
    </button>
  );
}

export default function ProjectsPage() {
  const navigate = useNavigate();
  const { data: projects, isLoading, refetch } = useProjectsList();
  const authStatus = useAuthStore((s) => s.status);
  const { data: cloudProjects, refetch: refetchCloud } = useCloudProjects(
    authStatus === 'authenticated',
  );
  const invalidateCloudProjects = useInvalidateCloudProjects();
  const pushToast = useUiStore((s) => s.pushToast);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [importing, setImporting] = useState(false);

  const canSaveToCloud = authStatus === 'authenticated';

  const handleImportFile = async (file: File) => {
    setImporting(true);
    try {
      const result = await importProjectBackup(file);
      if (result.status === 'ok') {
        pushToast({ kind: 'success', message: `“${result.project.title}” is geïmporteerd.` });
        await refetch();
        navigate(`/editor/${result.project.id}`);
      } else {
        pushToast({ kind: 'error', message: result.message });
      }
    } catch (err) {
      pushToast({
        kind: 'error',
        message: err instanceof Error ? err.message : t('errors.backupInvalid'),
      });
    } finally {
      setImporting(false);
    }
  };

  const handleSyncedToCloud = () => {
    void refetch();
    invalidateCloudProjects();
  };

  const handleCloudOpened = () => {
    void refetch();
    void refetchCloud();
  };

  return (
    <div className="page-shell">
      <SiteHeader />
      <div className="projects-page">
        <div className="projects-page-header">
          <h1>{t('projects.title')}</h1>
          <div className="projects-page-actions">
            <input
              ref={fileInputRef}
              type="file"
              accept=".fotoboek,application/zip"
              className="visually-hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) void handleImportFile(file);
                e.target.value = '';
              }}
            />
            <Button
              variant="secondary"
              onClick={() => fileInputRef.current?.click()}
              disabled={importing}
            >
              {importing ? <Spinner size={16} /> : <Upload size={16} />}
              {t('projects.importBackup')}
            </Button>
            <Button asChild variant="primary">
              <Link to="/nieuw">
                <Plus size={16} />
                {t('projects.newProject')}
              </Link>
            </Button>
          </div>
        </div>

        <section>
          <h2 className="projects-section-title">{t('projects.localSection')}</h2>
          {isLoading ? (
            <Spinner />
          ) : !projects || projects.length === 0 ? (
            <EmptyState
              icon={BookImage}
              title={t('projects.empty.title')}
              body={t('projects.empty.body')}
              action={
                <Button asChild variant="primary">
                  <Link to="/nieuw">{t('projects.empty.cta')}</Link>
                </Button>
              }
            />
          ) : (
            <div className="projects-grid">
              {projects.map((meta) => (
                <ProjectCard
                  key={meta.id}
                  meta={meta}
                  canSaveToCloud={canSaveToCloud}
                  onDeleted={refetch}
                  onSyncedToCloud={handleSyncedToCloud}
                />
              ))}
            </div>
          )}
        </section>

        {authStatus === 'authenticated' ? (
          <section>
            <h2 className="projects-section-title">
              <Cloud size={16} /> {t('projects.cloudSection')}
            </h2>
            {!cloudProjects || cloudProjects.length === 0 ? (
              <EmptyState title={t('projects.cloud.empty')} />
            ) : (
              <div className="projects-grid">
                {cloudProjects.map((p) => (
                  <CloudProjectCard key={p.id} project={p} onOpened={handleCloudOpened} />
                ))}
              </div>
            )}
          </section>
        ) : null}
      </div>
    </div>
  );
}
